/**
 * Page Context Tool Generator — produces get_page_context and get_visible_elements
 * tools that let agents discover current page info and interactive UI elements.
 */

import type { WebMCPTool } from "../core/tool-registry"

// ── Types ──────────────────────────────────────────────────

export interface PageContextRuntime {
  getCurrentPage(): { id: string; route: string; title?: string; blocks: string[] } | null
  blocks: Record<string, { ui?: { root?: UiNode } }>
  getAuthState?: () => { isAuthenticated: boolean; userName?: string; userType?: string } | null
}

interface UiNode {
  id: string
  type: string
  text?: string
  label?: string
  placeholder?: string
  events?: Record<string, unknown>
  children?: UiNode[]
}

interface VisibleElement {
  id: string
  type: string
  text?: string
  action?: string
}

// ── Interactive element types ──────────────────────────────

const INTERACTIVE_TYPES = new Set([
  "button",
  "input",
  "text_input",
  "select",
  "checkbox",
  "switch",
  "link",
  "icon_button",
  "radio",
])

// ── get_page_context ───────────────────────────────────────

export function generatePageContextTools(runtimeRef: PageContextRuntime): WebMCPTool[] {
  return [
    {
      name: "get_page_context",
      description: "Get current page context including route, title, and block types",
      inputSchema: { type: "object", properties: {} },
      handler: (_params: Record<string, unknown>) => {
        const page = runtimeRef.getCurrentPage()
        if (!page) return null

        const blockTypes: string[] = []
        for (const blockName of page.blocks) {
          const block = runtimeRef.blocks[blockName]
          const rootType = block?.ui?.root?.type
          if (rootType) blockTypes.push(rootType)
        }

        let auth: { isAuthenticated: boolean; userName?: string; userType?: string } | null = null
        try { auth = runtimeRef.getAuthState?.() ?? null } catch { /* state not ready */ }

        return {
          route: page.route,
          pageTitle: page.title ?? page.id,
          pageId: page.id,
          blockTypes,
          auth: auth ? {
            isAuthenticated: auth.isAuthenticated,
            userName: auth.userName,
            userType: auth.userType,
          } : { isAuthenticated: false },
        }
      },
      metadata: { readOnly: true, tags: ["discovery"] },
    },
  ]
}

// ── get_visible_elements ───────────────────────────────────

export function generateVisibleElementsTools(runtimeRef: PageContextRuntime): WebMCPTool[] {
  return [
    {
      name: "get_visible_elements",
      description: "Get all interactive elements (buttons, inputs, links) visible on the current page",
      inputSchema: { type: "object", properties: {} },
      handler: (_params: Record<string, unknown>) => {
        const page = runtimeRef.getCurrentPage()
        if (!page) return { elements: [], count: 0 }

        const elements: VisibleElement[] = []

        for (const blockName of page.blocks) {
          const block = runtimeRef.blocks[blockName]
          const root = block?.ui?.root
          if (root) walkForInteractiveElements(root, elements)
        }

        return { elements, count: elements.length }
      },
      metadata: { readOnly: true, tags: ["discovery"] },
    },
  ]
}

// ── Tree walker ────────────────────────────────────────────

function walkForInteractiveElements(node: UiNode, out: VisibleElement[]): void {
  if (INTERACTIVE_TYPES.has(node.type)) {
    const el: VisibleElement = {
      id: node.id,
      type: node.type,
    }

    // Text: try text, label, placeholder
    const text = node.text ?? node.label ?? node.placeholder
    if (text) el.text = text

    // Action: try tap or click event flowName
    if (node.events) {
      const tapEvent = node.events.tap as { flowName?: string } | string | undefined
      const clickEvent = node.events.click as { flowName?: string } | string | undefined
      const event = tapEvent ?? clickEvent
      if (event) {
        el.action = typeof event === "string" ? event : event.flowName
      }
    }

    out.push(el)
  }

  // Recurse into children
  for (const child of node.children ?? []) {
    walkForInteractiveElements(child, out)
  }
}
